import React from "react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

export interface TopicStepData {
  topic: string;
  difficulty: "beginner" | "intermediate" | "advanced";
  goal: "overview" | "university" | "deep";
  additionalInstructions: string;
}

interface TopicStepProps {
  data: TopicStepData;
  onChange: (data: TopicStepData) => void;
}

const SUGGESTED_TOPICS = [
  "Blockchain Technology",
  "Machine Learning Basics",
  "Financial Accounting",
  "Organic Chemistry",
  "World History",
  "Data Structures",
];

export const TopicStep: React.FC<TopicStepProps> = ({ data, onChange }) => {
  const update = (partial: Partial<TopicStepData>) =>
    onChange({ ...data, ...partial });

  return (
    <div className="flex flex-col gap-8">
      {/* Topic input */}
      <div className="space-y-3">
        <Label className="text-sm font-medium">What do you want to learn?</Label>
        <textarea
          placeholder="What would you like to learn about? e.g. Introduction to blockchain technology..."
          value={data.topic}
          onChange={(e) => update({ topic: e.target.value })}
          rows={2}
          className="text-base rounded-xl border border-border/60 bg-background px-4 py-3 w-full resize-none focus:outline-none focus:ring-1 focus:ring-foreground/20 placeholder:text-muted-foreground/60"
        />
        <div className="flex flex-wrap gap-2 pt-2">
          {SUGGESTED_TOPICS.map((topic) => (
            <button
              key={topic}
              type="button"
              onClick={() => update({ topic })}
              className="rounded-full border border-border bg-muted px-3 py-1.5 text-xs text-muted-foreground transition-all duration-200 hover:bg-accent hover:text-foreground"
            >
              {topic}
            </button>
          ))}
        </div>
      </div>

      {/* Difficulty — segmented pills */}
      <div className="space-y-3">
        <Label className="text-sm font-medium">Difficulty</Label>
        <div className="flex gap-2">
          {(["beginner", "intermediate", "advanced"] as const).map((level) => (
            <button
              key={level}
              type="button"
              onClick={() => update({ difficulty: level })}
              className={`rounded-full px-4 py-2 border text-sm capitalize transition-all duration-200 cursor-pointer ${
                data.difficulty === level
                  ? "bg-foreground text-background border-foreground"
                  : "border-border hover:bg-muted"
              }`}
            >
              {level}
            </button>
          ))}
        </div>
      </div>

      {/* Goal */}
      <div className="space-y-3">
        <Label className="text-sm font-medium">Learning Goal</Label>
        <RadioGroup
          value={data.goal}
          onValueChange={(v) => update({ goal: v as TopicStepData["goal"] })}
          className="flex flex-col gap-3"
        >
          {[
            { value: "overview", label: "Quick overview", desc: "Get the big picture fast" },
            { value: "university", label: "University-style", desc: "Structured, in-depth curriculum" },
            { value: "deep", label: "Deep mastery", desc: "Comprehensive with practice and review" },
          ].map((opt) => (
            <div
              key={opt.value}
              className="flex items-start space-x-3 rounded-xl border border-border p-4 has-[:checked]:border-primary/40 has-[:checked]:bg-primary/5 transition-all duration-200 hover:border-foreground/20 hover:bg-muted/30"
            >
              <RadioGroupItem value={opt.value} id={`goal-${opt.value}`} className="mt-0.5" />
              <Label htmlFor={`goal-${opt.value}`} className="flex flex-col cursor-pointer">
                <span className="text-sm font-semibold">{opt.label}</span>
                <span className="text-xs text-muted-foreground">{opt.desc}</span>
              </Label>
            </div>
          ))}
        </RadioGroup>
      </div>

      {/* Additional instructions */}
      <div className="space-y-3">
        <Label className="text-sm font-medium">
          Additional instructions{" "}
          <span className="font-normal text-muted-foreground">(optional)</span>
        </Label>
        <textarea
          placeholder="Any specific focus areas, examples, or preferences..."
          value={data.additionalInstructions}
          onChange={(e) => update({ additionalInstructions: e.target.value })}
          rows={2}
          className="text-base rounded-xl border border-border/50 bg-background px-4 py-3 w-full resize-none focus:outline-none focus:ring-1 focus:ring-foreground/20 placeholder:text-muted-foreground/60"
        />
      </div>
    </div>
  );
};
